/**
 * Hook for fetching and caching hardware capabilities
 */

import { useCallback, useEffect, useState } from 'react';

import { getHardwareCapabilities } from '../services/componentService';
import type { HardwareCapabilities } from '../services/componentService';
import { RetryHandler } from '../services/api-adapters/RetryHandler';

import { handleError } from '../utils/errorHandler';

interface UseHardwareCapabilitiesResult {
  hardware: HardwareCapabilities | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useHardwareCapabilities(): UseHardwareCapabilitiesResult {
  const [hardware, setHardware] = useState<HardwareCapabilities | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHardware = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Retry transparently for cold-backend races (uvicorn still booting,
      // Cloud Run cold start). Without this, a single timing-window failure
      // freezes the page on "Failed to detect hardware" until manual reload.
      const data = await RetryHandler.withRetry(() => getHardwareCapabilities());
      setHardware(data);
    } catch (e) {
      console.error('Failed to fetch hardware capabilities:', e);
      setError(handleError(e, 'HardwareCapabilities', 'Failed to fetch hardware capabilities'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHardware();
  }, [fetchHardware]);

  return { hardware, loading, error, refresh: fetchHardware };
}
